<template>
  <main class="blog-page">
    <div class="blog-wrapper">
      <UiBreadCrumbsV2
        :items="[{ title: t('blog'), link: localePath('/blog') }]"
        class="blog-page-breadcrumbs"
      />

      <div
        v-if="isAdmin"
        class="f fe gap16 card-dark mb32"
      >
        <nuxt-link
          :to="localePath('/blog/moderation/new')"
          class="btn btn-violet gap8"
        >
          <span
            class="f24"
            style="line-height: 0"
          >+ </span>{{ t('create_article') }} (EN)
        </nuxt-link>
        <nuxt-link
          :to="localePath('/blog/moderation')"
          class="btn btn-primary"
        >
          {{ t('moderation') }}
        </nuxt-link>
      </div>
      <p class="colored-title">
        CSMC {{ t('blog') }}
      </p>
      <h1>
        {{ t('blog_subtitle') }}
      </h1>
      <p class="mb32">
        {{ t('blog_desc') }}
      </p>

      <template v-if="!showEmptyError">
        <PagesBlogMainArtiles
          @on-empty="onEmpty"
          @on-posts-loaded="updatePostsSchemas"
        />

        <PagesBlogCategorizedArticles
          v-for="category in store.categories"
          :key="category.title"
          :category="category"
          @on-posts-loaded="updatePostsSchemas"
        />
      </template>

      <section
        v-else
        class="flex"
      >
        <LazyUiNoResult class="ma">
          {{ t('no_blog_articles') }}
        </LazyUiNoResult>
      </section>
    </div>

    <UiScrollTopBtn />
  </main>
</template>

<script lang="ts" setup>
import { useAlternateLinks } from '~/composables/alternate-links';
import { useStore } from '~/store';
import { useBlogStore } from '~/store/blog';

definePageMeta({
  layout: 'no-categories',
});

const localePath = useLocalePath();
const { t, locale } = useI18n();
const schemas = ref<any[]>([]);
const mainStore = useStore();

const isAdmin = computed(() => mainStore.user?.is_admin);

const { data: categories, error } = await useAsyncData(`blogCategories`, () => getBlogCategories(), {
  transform: input => input?.data?.getBlogCategories,
});

if (error.value) {
  console.log(`Blog page fetch error: ${error.value?.message}` || 'Not found');
}

const store = useBlogStore();
store.categories = categories.value || [];
const showEmptyError = ref(false);

function onEmpty() {
  showEmptyError.value = true;
}

function updatePostsSchemas(schemasArg: any[]) {
  schemas.value = [...schemas.value, ...schemasArg];
}

const { alternateLinks, canonical } = useAlternateLinks('/blog');

useHead({
  meta: [
    {
      name: 'keywords',
      content: 'steam, counter strike global offensive, csgo, cs2, skins, items, blog, posts',
    },
  ],
  link: () => alternateLinks.value,
});

useSeoMeta({
  title: () => t('heads.blog.title'),
  ogTitle: () => t('heads.blog.title'),
  description: () => t('heads.blog.description'),
  ogDescription: () => t('heads.blog.description'),
  ogImage: () => 'https://csmarketcap.com/img/logo-bg-black.png',
  twitterCard: 'summary_large_image',
  twitterTitle: () => t('heads.blog.title'),
  twitterDescription: () => t('heads.blog.description'),
  twitterImage: 'https://csmarketcap.com/img/logo-bg-black.png',
  ogSiteName: 'CSMarketCap',
  ogUrl: () => canonical.value,
  ogType: 'website',
  ogLocale: () => locale.value,
});

useSchemaOrg([
  defineWebPage({
    '@type': 'WebPage',
    'url': 'https://csmarketcap.com/blog',
    'name': t('heads.blog.title'),
    'description': t('heads.blog.description'),
    'image': () => 'https://csmarketcap.com/img/logo-bg-black.png',
    'publisher': {
      '@type': 'Organization',
      'name': 'CS Market Cap',
      'logo': {
        '@type': 'ImageObject',
        'url': 'https://csmarketcap.com/img/logo.svg',
      },
    },
    'mainEntityOfPage': {
      '@type': 'WebPage',
      '@id': 'https://csmarketcap.com/blog',
    },
  }),
  defineItemList({
    '@type': 'ItemList',
    'name': t('heads.blog.title'),
    'itemListElement': () => schemas.value,
  }),
  defineBreadcrumb({
    itemListElement: [
      { name: t('main'), item: '/' },
      { name: t('blog'), item: localePath('/blog') },
    ],
  }),
]);
</script>

<style lang="scss" scoped>
@use '/assets/scss/ui/blog.scss';
</style>
